import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { precargaGet, precargaPost, precargaPatch } from "../utils/PrecargaApi";
import { useAuth } from "./useAuth";

/**
 * Custom hook for managing Precarga Events
 * @param {object} options - Query options
 * @param {number} options.page - Page number (default 1)
 * @param {number} options.pageSize - Page size (default 20)
 * @param {string} options.status - Filter by status
 * @param {number} options.cashlessEventId - Filter by cashless event ID
 * @param {string} options.slug - Filter by slug
 * @param {number} options.eventId - Specific event ID to fetch
 */
export function usePrecargaEvents({
  page = 1,
  pageSize = 20,
  status,
  cashlessEventId,
  slug,
  eventId,
} = {}) {
  const { user } = useAuth();
  const token = user?.token;
  const queryClient = useQueryClient();

  // Get precarga event by cashless event ID (1:1 relationship)
  const eventByCashlessIdQuery = useQuery({
    queryKey: ["precargaEventByCashlessId", cashlessEventId],
    queryFn: async () => {
      const response = await precargaGet(`events/by-cashless/${cashlessEventId}`);
      // Return the first item if exists, null otherwise
      return response?.precargaEvent || null;
    },
    enabled: !!token && !!cashlessEventId,
  });

  // Get single precarga event by ID
  const eventQuery = useQuery({
    queryKey: ["precargaEvent", eventId],
    queryFn: async () => {
      return await precargaGet(`events/${eventId}`);
    },
    enabled: !!token && !!eventId,
  });

  // List precarga events with pagination (for admin views)
  const listQuery = useQuery({
    queryKey: ["precargaEvents", { page, pageSize, status, cashlessEventId, slug }],
    queryFn: async () => {
      const params = { page, pageSize };
      if (status) params.status = status;
      if (cashlessEventId) params.cashlessEventId = cashlessEventId;
      if (slug) params.slug = slug;

      return await precargaGet("events", params);
    },
    enabled: !!token && !cashlessEventId, // Only for listing, not for single event lookup
    keepPreviousData: true,
  });

  // Create precarga event mutation
  const createMutation = useMutation({
    mutationFn: async (data) => {
      return await precargaPost("events", data);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(["precargaEvents"]);
      // Invalidate the cashless event ID lookup so sidebar updates
      if (data?.cashlessEventId) {
        queryClient.invalidateQueries(["precargaEventByCashlessId", data.cashlessEventId]);
      }
    },
  });

  // Update precarga event mutation
  const updateMutation = useMutation({
    mutationFn: async ({ eventId, data }) => {
      return await precargaPatch(`events/${eventId}`, data);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(["precargaEvents"]);
      queryClient.invalidateQueries(["precargaEvent", data.id]);
      // Invalidate the cashless event ID lookup so sidebar updates
      if (data?.cashlessEventId) {
        queryClient.invalidateQueries(["precargaEventByCashlessId", data.cashlessEventId]);
      }
    },
  });

  // Disable precarga event (set status to DISABLED)
  const disableMutation = useMutation({
    mutationFn: async ({ eventId, reason }) => {
      return await precargaPatch(`events/${eventId}`, {
        status: "DISABLED",
        disabledReason: reason,
      });
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(["precargaEvents"]);
      // Invalidate the cashless event ID lookup so sidebar updates
      if (data?.cashlessEventId) {
        queryClient.invalidateQueries(["precargaEventByCashlessId", data.cashlessEventId]);
      }
    },
  });

  // Enable precarga event (set status to ACTIVE)
  const enableMutation = useMutation({
    mutationFn: async (eventId) => {
      return await precargaPatch(`events/${eventId}`, {
        status: "ACTIVE",
      });
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(["precargaEvents"]);
      // Invalidate the cashless event ID lookup so sidebar updates
      if (data?.cashlessEventId) {
        queryClient.invalidateQueries(["precargaEventByCashlessId", data.cashlessEventId]);
      }
    },
  });

  return {
    // Single event by cashless ID (primary use case)
    precargaEvent: eventByCashlessIdQuery.data,
    isLoadingPrecargaEvent: eventByCashlessIdQuery.isLoading,
    isErrorPrecargaEvent: eventByCashlessIdQuery.isError,
    errorPrecargaEvent: eventByCashlessIdQuery.error,
    hasPrecargaEvent: !!eventByCashlessIdQuery.data,

    // List queries (for admin views)
    events: listQuery.data?.items || [],
    totalCount: listQuery.data?.totalCount || 0,
    hasMore: listQuery.data?.hasMore || false,
    isLoading: listQuery.isLoading,
    isError: listQuery.isError,
    error: listQuery.error,

    // Single event query by ID
    event: eventQuery.data,
    isLoadingEvent: eventQuery.isLoading,
    isErrorEvent: eventQuery.isError,
    errorEvent: eventQuery.error,

    // Mutations
    createMutation,
    createEvent: createMutation.mutate,
    isCreating: createMutation.isLoading,

    updateMutation,
    updateEvent: updateMutation.mutate,
    isUpdating: updateMutation.isLoading,

    disableMutation,
    disableEvent: disableMutation.mutate,
    isDisabling: disableMutation.isLoading,

    enableMutation,
    enableEvent: enableMutation.mutate,
    isEnabling: enableMutation.isLoading,

    // Utility
    refetch: listQuery.refetch,
  };
}

export default usePrecargaEvents;
